import { useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import invert from 'lodash/invert';
import { useRouter } from 'next/router';

import { ActiveTabSearchKey, FocusTabHashKey } from '../constants/tab';
import { PAGE_ID, TabType, TabTypeValue } from '../constants/tab_menu';
import { useCatalogProductIdParams } from '../hooks/useCatalogProductIdParams';
import { atomWithSearch } from '../utils/atomWithSearch';

const INVERT_PAGE_ID = invert(PAGE_ID) as unknown as Record<string, TabType>;

export function getInitialActiveTabType(initSearch?: string) {
  const searchParams = new URLSearchParams(initSearch);
  const active_tab_search = searchParams.get(ActiveTabSearchKey);
  return [active_tab_type_atom.originAtom, active_tab_search] as const;
}

export function getFocusTabUrl(tab_type: TabType, encode_hash: boolean) {
  return window.location.origin + window.location.pathname + getFocusTabQuery(tab_type, encode_hash);
}

export function getFocusTabQuery(tab_type: TabType, encode_hash: boolean) {
  return `?${ActiveTabSearchKey}=${PAGE_ID[tab_type]}${encode_hash ? encodeURIComponent('#') : '#'}${FocusTabHashKey}`;
}

const getActiveTabUrl = (tabType: TabType | keyof typeof TabType) => {
  if (typeof window === 'undefined') {
    return '';
  }
  const url = new URL(window.location.href);
  if (typeof tabType === 'string') {
    url.searchParams.set(ActiveTabSearchKey, PAGE_ID[TabTypeValue[tabType]]);
  } else {
    url.searchParams.set(ActiveTabSearchKey, PAGE_ID[tabType]);
  }
  return url.toString();
};

const active_tab_type_atom = atomWithSearch<TabType>(ActiveTabSearchKey, TabType.PRODUCT_DETAIL_INFO, {
  serialize: (val) => {
    const page_key = typeof val === 'number' ? val : INVERT_PAGE_ID[val];
    const serialized = PAGE_ID[page_key];
    return serialized;
  },
  deserialize: (str) => {
    const deserialized = Number(INVERT_PAGE_ID[str]);
    return deserialized;
  },
  setSearch: 'replaceState',
});

export function useActiveTabTypeAtom(option?: { onUpdate?: (delay?: number, tabType?: TabType) => void }) {
  const router = useRouter();
  const tabRef = useRef<string | undefined>(router.query.tab?.toString().toLowerCase());

  const [active_tab_type] = useAtom(active_tab_type_atom);

  useEffect(() => {
    const handleRouteChangeStart = () => {
      tabRef.current = router.query.tab?.toString().toLowerCase();
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router.query.tab, router.events]);

  useEffect(() => {
    const handleRouteChangeComplete = (url: string) => {
      const searchParams = new URLSearchParams(url.split('?')[1]);

      const focusTabValue = searchParams.get(FocusTabHashKey);
      if (focusTabValue === FocusTabHashKey) {
        return;
      }

      const tabValue = searchParams.get(ActiveTabSearchKey)?.toLowerCase();

      if (!tabValue) {
        return;
      }

      option?.onUpdate?.();
      tabRef.current = tabValue;
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events]);

  useEffect(() => {
    const hashValue = window.location.hash.substring(1);
    if (hashValue === FocusTabHashKey) {
      window.history.replaceState({ [FocusTabHashKey]: FocusTabHashKey }, '', ' ');

      option?.onUpdate?.(500);
    }
  }, [option, router]);

  const update = (tabType: TabType | keyof typeof TabType) => {
    const resolvedTabType = typeof tabType === 'string' ? TabTypeValue[tabType] : tabType;
    option?.onUpdate?.(undefined, resolvedTabType);
    router.replace(getActiveTabUrl(tabType), undefined, { shallow: true, scroll: false });
  };

  return [active_tab_type, update] as const;
}

export function useGoToReviewList() {
  const { push } = useRouter();
  const { catalog_product_id } = useCatalogProductIdParams();

  const goToReviewList = () => {
    push('/review/list/' + catalog_product_id);
  };
  return { goToReviewList };
}
